import { useCallback, useEffect, useState, useRef } from 'react';
import { useLoadingHooks } from '@shakkerai/pro-hooks';
import {
  requestUserStorage,
  requestTaskDetail,
  postAblumList,
  postAblumDetail,
  postHistoryHome,
  getSharePicState,
  setSharePicState,
} from '@shakkerai/services';
import { useInfiniteScroll } from 'ahooks';
import { eventBus } from '@starflow/app/helpers';

const PAGE_SIZE = 30;

export const useScrollHooks = (scrollContainerRef) => {
  const [isScrollTop, setIsScrollTop] = useState(true);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.addEventListener('scroll', (event) => {
        const scrollTop = event.target.scrollTop;
        setIsScrollTop(scrollTop === 0);
      });
    }
  }, [setIsScrollTop, scrollContainerRef]);

  return {
    isScrollTop,
  };
};

/**
 * 生成历史列表
 *
 * @param {*} scrollContainerRef
 * @returns
 */
export const useGenHistoryHooks = (scrollContainerRef, keywordsRef, sortRef, dateRef) => {
  const genNewParams = () => {
    return {
      keyword: keywordsRef.current,
      sort: sortRef.current,
      createDate: dateRef.current,
    };
  };

  const { data, ...rest } = useAbstractListRequest(
    postAblumDetail,
    scrollContainerRef,
    genNewParams,
    'event_historyList',
  );
  const { loadTotal } = useOnlySizeRequestHooks(
    postAblumDetail,
    genNewParams,
    'event_historyListOnNewRecords',
    data?.total,
  );

  return {
    data,
    ...rest,
    loadTotal,
  };
};

/**
 * 设置/获取图片公开状态
 *
 * @param {*}
 * @returns
 */
export const useSharePicHooks = () => {
  const [shareState, setShareState] = useState(false);
  const getSharePicStates = useCallback(async () => {
    try {
      const responseData = await getSharePicState();
      setShareState(responseData.data);
    } catch (error) {
      console.error(error);
    }
  }, []);
  const setSharePicStates = useCallback(async (params) => {
    try {
      const responseData = await setSharePicState(params);
      return responseData;
    } catch (error) {
      console.error(error);
      return {};
    }
  }, []);
  useEffect(() => {
    getSharePicStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    shareState,
    setSharePicStates,
  };
};

/**
 * 生成历史列表首页
 *
 * @param {*} scrollContainerRef
 * @returns
 */
export const useGenHistoryHomeHooks = (scrollContainerRef, sortRef) => {
  const [latestData, setLatestData] = useState();
  const genNewParams = () => {
    return {
      sort: sortRef.current,
      pageSize: 1000,
      createDate: latestData?.day || '',
    };
  };

  const { data, ...rest } = useAbstractListRequest(
    postHistoryHome,
    scrollContainerRef,
    genNewParams,
    'event_historyList_home',
  );

  useEffect(() => {
    if (data) {
      let datas = data?.list || [];
      let newData = datas[0];
      if (sortRef.current !== -1) {
        newData = datas?.slice(-1)?.[0];
      }
      setLatestData(newData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    data,
    ...rest,
  };
};

/**
 * 专辑列表
 */
export const useAblumListHooks = (scrollContainerRef, keywordsRef, sortRef) => {
  // 目前专辑列表的接口只是为了获得精选专辑的专辑id，因此不需要被其它条件干扰
  const genNewParams = () => {
    return {
      keyword: keywordsRef.current,
      sort: sortRef.current,
    };
  };

  return useAbstractListRequest(postAblumList, scrollContainerRef, genNewParams, 'event_ablum_list');
};

/**
 * 专辑详情
 *
 * @param {*} scrollContainerRef
 * @returns
 */
export const useAblumDetailHooks = (scrollContainerRef, keywordsRef, sortRef, groupId) => {
  const genNewParams = () => {
    const params = {
      keyword: keywordsRef.current,
      sort: sortRef.current,
      groupId,
    };
    return params;
  };
  // 需要先通过专辑列表接口获得专辑详情的id，再请求专辑详情
  const { data, ...rest } = useAbstractListRequest(
    postAblumDetail,
    scrollContainerRef,
    genNewParams,
    'event_ablumDetail',
  );

  const { loadTotal } = useOnlySizeRequestHooks(
    postAblumDetail,
    genNewParams,
    'event_ablumDetailOnNewRecords',
    data?.total,
  );

  return {
    data,
    ...rest,
    loadTotal,
  };
};

/**
 * 用来解析精选专辑
 */
export const usePremiumAblumHooks = () => {
  const [premiumAblumId, setPremiumAblumId] = useState();

  const premiumAblumLoad = useCallback(async () => {
    try {
      const responseData = await postAblumList({
        pageNo: 1,
        pageSize: PAGE_SIZE,
      });

      if (responseData.code === 0) {
        const { list } = responseData.data;
        const premiumAblumGroup = list?.find((item) => item.initialGroup);

        if (premiumAblumGroup) {
          setPremiumAblumId(premiumAblumGroup.groupId);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, []);

  return {
    premiumAblumLoad,
    premiumAblumId,
  };
};

/**
 * 只负责完成数量的请求
 * @param {*} abstractRequestFunction
 */
export const useOnlySizeRequestHooks = (abstractRequestFunction, paramsResolve, eventLabel, renderTotal = 0) => {
  const dataRequest = useCallback(async () => {
    try {
      const params = paramsResolve();
      const responseData = await abstractRequestFunction({
        pageNo: 1,
        pageSize: 1,
        ...params,
        sizeLoad: eventLabel,
      });

      if (responseData.code === 0) {
        eventBus.emit(eventLabel, responseData.data?.total - renderTotal);
      }
    } catch (error) {
      console.error(error);
    }
  }, [abstractRequestFunction, renderTotal, eventLabel, paramsResolve]);

  return {
    loadTotal: dataRequest,
  };
};

/**
 * 抽象的数据请求方法，包含了手动加载，分页，虚拟列表等公共能力
 *
 * @param {*} scrollContainerRef
 * @param {*} scrollWrapperRef
 * @param {*} keywordsRef
 * @param {*} sortRef
 * @returns
 */
const useAbstractListRequest = (abstractRequestFunction, scrollContainerRef, paramsResolve, removeEventLabel) => {
  const pageSize = useRef(PAGE_SIZE);

  const dataRequest = useCallback(
    async (pageNo) => {
      try {
        const params = paramsResolve();
        const responseData = await abstractRequestFunction({
          pageNo,
          pageSize: PAGE_SIZE,
          ...params,
          dataReload: removeEventLabel,
        });

        if (responseData.code === 0) {
          return responseData.data;
        } else {
          return [];
        }
      } catch (error) {
        console.error(error);
      }
    },
    [abstractRequestFunction, paramsResolve, removeEventLabel],
  );

  const { data, loading, loadingMore, noMore, reload, mutate } = useInfiniteScroll(
    (d) => {
      const pageNo = d?.pageNo + 1 || 1;
      return dataRequest(pageNo);
    },
    {
      target: scrollContainerRef,
      manual: true,
      threshold: 900,
      isNoMore: (d) => {
        // 没有数据时，防止命中加载下一页
        const isNoMore = d?.list.length >= (d?.total || 0);
        return isNoMore;
      },
    },
  );

  useEffect(() => {
    if (data) {
      // 刷新的时候一次加载请求到的数量
      pageSize.current = Math.max(data?.list.length || PAGE_SIZE, PAGE_SIZE);
    }
  }, [data]);

  /**
   * 列表删除逻辑
   */
  const removeDeletedItem = useCallback(
    (deletedGroupItem, deletedIndex) => {
      // 被删除的图片位于哪个组下的哪个图片
      if (data?.list) {
        // 一组内的图片删除
        if (deletedGroupItem.images.length > 1) {
          deletedGroupItem.images.splice(deletedIndex, 1);
        } else {
          // const index = data.list.findIndex((item) => item.generateId === deletedGroupItem.generateId);
          // data.list.splice(index, 1);
          // 这是在解决渲染数量和实际数量总数不一致的问题
          deletedGroupItem.deleted = true;
        }
        mutate({
          ...data,
        });
      }
    },
    [data, mutate],
  );

  /**
   * 对数据进行批处理操作
   */
  const batTackleItems = useCallback(
    (eventType, tackledIds) => {
      if (data?.list) {
        data.list.forEach((taskItem) => {
          if (eventType === 'colected') {
            taskItem.images.forEach((image) => {
              const isFound = tackledIds.includes(image.outputId);
              if (isFound) {
                image.groupId = 100; // 收藏，随便设置的一个值，只要大于0就代表收藏
              }
            });
          }
          if (eventType === 'uncolected') {
            taskItem.images.forEach((image) => {
              const isFound = tackledIds.includes(image.outputId);
              if (isFound) {
                image.groupId = 0; // 取消收藏
              }
            });
          } else if (eventType === 'deleted') {
            const remainItems = taskItem.images.filter((image) => !tackledIds.includes(image.outputId));
            if (remainItems.length === 0) {
              taskItem.deleted = true;
            } else {
              taskItem.images = remainItems;
            }
          }
        });

        mutate({
          ...data,
        });
      }
    },
    [data, mutate],
  );

  useEffect(() => {
    if (removeEventLabel) {
      const batEventLable = removeEventLabel + 'bat';

      eventBus.on(removeEventLabel, removeDeletedItem);
      eventBus.on(batEventLable, batTackleItems);

      return () => {
        eventBus.off(removeEventLabel, removeDeletedItem);
        eventBus.off(batEventLable, batTackleItems);
      };
    }
  }, [removeDeletedItem, removeEventLabel, batTackleItems]);

  return {
    data,
    loading,
    loadingMore,
    noMore,
    reload,
  };
};

/**
 * 用户存储空间
 *
 * @returns
 */
export const useStorageHooks = () => {
  const [storageInfo, setStoreageInfo] = useState({ usedCount: 0, threshold: 1 });

  const loadUserStorage = useCallback(async () => {
    const response = await requestUserStorage();

    if (response.code === 0) {
      setStoreageInfo(response.data);
    }
  }, []);

  useEffect(() => {
    loadUserStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    storageInfo,
    reloadStoreage: loadUserStorage,
  };
};

/**
 * 任务详情
 * @param {*} generateId
 * @param {*} groupId -1，对图片不做过滤， 大于-1的情况，对图片做过滤
 * @returns
 */
export const useTaskDetail = (generateId, groupId = -1) => {
  const [taskDetail, setTaskDetail] = useState();

  const { setLoadingState, setSuccessState, setFailState, ...restState } = useLoadingHooks();

  const loadTaskDetail = useCallback(
    async (showLoading = true) => {
      showLoading && setLoadingState();
      const response = await requestTaskDetail({
        generateId,
      });

      if (response.code === 0) {
        if (groupId === -1) {
          setTaskDetail(response.data);
        } else {
          // 图片过滤
          const primitiveImages = response.data.images;
          response.data.images = primitiveImages.filter((item) => item.groupId === groupId);
          setTaskDetail(response.data);
        }

        setSuccessState();
      } else {
        setFailState();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [groupId, generateId, setFailState, setLoadingState, setSuccessState],
  );

  return {
    loadTaskDetail,
    taskDetail,
    ...restState,
  };
};
